<template>
  <div class="TheNavbarMobile">
    <button
      class="TheNavbarMobile__toggle"
      @click="toggleMenu"
      :class="{ 'TheNavbarMobile__toggle--is-open': isMenuOpen }"
    >
      <UIIcon
        class="TheNavbarMobile__toggle-icon"
        :name="isMenuOpen ? 'close' : 'menu'"
        :key="isMenuOpen ? 'close' : 'menu'"
      />
    </button>

    <!-- Mobile Menu Overlay -->
    <div v-if="isMenuOpen" class="TheNavbarMobile__overlay" @click="closeMenu">
      <div class="TheNavbarMobile__menu" @click.stop>
        <!-- Navigation Links -->
        <nav class="TheNavbarMobile__nav">
          <ul class="TheNavbarMobile__nav-list">
            <li
              v-for="link in links"
              :key="link.label"
              class="TheNavbarMobile__nav-item"
            >
              <!-- Link with dropdown -->
              <div
                v-if="link.items"
                class="TheNavbarMobile__nav-link-container"
              >
                <button
                  class="TheNavbarMobile__nav-link"
                  @click="toggleDropdown(link.label)"
                  :class="{
                    'TheNavbarMobile__nav-link--is-open':
                      openDropdown === link.label,
                  }"
                >
                  <span class="TheNavbarMobile__nav-link-text">
                    {{ link.label }}
                  </span>
                  <UIIcon
                    class="TheNavbarMobile__nav-link-icon"
                    :name="
                      openDropdown === link.label ? 'chevronUp' : 'chevronDown'
                    "
                  />
                </button>

                <!-- Dropdown Content -->
                <div
                  v-if="openDropdown === link.label"
                  class="TheNavbarMobile__dropdown"
                >
                  <ul class="TheNavbarMobile__dropdown-list">
                    <li
                      v-for="item in link.items"
                      :key="item.label"
                      class="TheNavbarMobile__dropdown-item"
                    >
                      <RouterLink
                        v-if="item.link"
                        :to="item.link"
                        class="TheNavbarMobile__dropdown-link"
                        @click="closeMenu"
                      >
                        <div class="TheNavbarMobile__dropdown-link-content">
                          <div class="TheNavbarMobile__dropdown-link-label">
                            {{ item.label }}
                          </div>
                          <div
                            v-if="item.description"
                            class="TheNavbarMobile__dropdown-link-description"
                          >
                            {{ item.description }}
                          </div>
                        </div>
                      </RouterLink>
                    </li>
                  </ul>
                </div>
              </div>

              <!-- Simple link without dropdown -->
              <RouterLink
                v-else-if="link.link"
                :to="link.link"
                class="TheNavbarMobile__nav-link TheNavbarMobile__nav-link--simple"
                @click="closeMenu"
              >
                <span class="TheNavbarMobile__nav-link-text">
                  {{ link.label }}
                </span>
              </RouterLink>
            </li>
          </ul>
        </nav>

        <!-- CTA Actions -->
        <div class="TheNavbarMobile__actions">
          <UIButton
            class="TheNavbarMobile__action"
            size="lg"
            theme="primary"
            :to="{ name: 'Contact' }"
          >
            Get in touch
          </UIButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { RouterLink } from 'vue-router'
import UIIcon from '../../../icons/UIIcon.vue'
import UIButton from '../../../buttons/UIButton.vue'
import { INavbarItem } from '../TheNavbar.vue'

defineProps<{
  links: INavbarItem[]
}>()

const isMenuOpen = ref(false)
const openDropdown = ref<string | null>(null)

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
  if (!isMenuOpen.value) {
    openDropdown.value = null
  }
}

const closeMenu = () => {
  isMenuOpen.value = false
  openDropdown.value = null
}

const toggleDropdown = (label: string) => {
  openDropdown.value = openDropdown.value === label ? null : label
}
</script>

<style lang="scss" scoped>
.TheNavbarMobile {
  position: relative;

  .TheNavbarMobile__toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border: none;
    background: none;
    cursor: pointer;
    border-radius: 0.5rem;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: $color-ui-gray-cool-50;
    }

    &.TheNavbarMobile__toggle--is-open {
      background-color: $color-ui-gray-cool-100;
    }
  }

  .TheNavbarMobile__toggle-icon {
    --size: 1.5rem;
    color: $color-ui-gray-cool-700;
  }

  .TheNavbarMobile__overlay {
    position: fixed;
    top: 70px;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    display: flex;
    flex-direction: column;
  }

  .TheNavbarMobile__menu {
    background-color: $color-white;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }

  .TheNavbarMobile__logo {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
  }

  .TheNavbarMobile__logo-text {
    @include font-size-18px;
    font-weight: 700;
    color: $color-ui-gray-cool-900;
    letter-spacing: 0.025em;
  }

  .TheNavbarMobile__logo-text--accent {
    color: $color-ui-accent-main;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(
        90deg,
        $color-ui-accent-main,
        $color-ui-brand-200
      );
      border-radius: 1px;
    }
  }

  .TheNavbarMobile__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border: none;
    background: none;
    cursor: pointer;
    border-radius: 0.5rem;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: $color-ui-gray-cool-50;
    }
  }

  .TheNavbarMobile__close-icon {
    --size: 1.5rem;
    color: $color-ui-gray-cool-700;
  }

  .TheNavbarMobile__nav {
    flex: 1;
    padding: 0;
  }

  .TheNavbarMobile__nav-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .TheNavbarMobile__nav-item {
    border-bottom: 1px solid $color-ui-gray-cool-200;

    &:last-child {
      border-bottom: none;
    }
  }

  .TheNavbarMobile__nav-link-container {
    width: 100%;
  }

  .TheNavbarMobile__nav-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 1.25rem 1.5rem;
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    transition: all 0.2s ease;

    &:hover {
      background-color: $color-ui-gray-cool-50;
    }

    &.TheNavbarMobile__nav-link--is-open {
      background-color: $color-ui-gray-cool-50;
    }

    &.TheNavbarMobile__nav-link--simple {
      text-decoration: none;
      color: inherit;
    }
  }

  .TheNavbarMobile__nav-link-text {
    @include font-size-16px;
    font-weight: 500;
    color: $color-ui-gray-cool-900;
  }

  .TheNavbarMobile__nav-link-icon {
    --size: 1rem;
    color: $color-ui-gray-cool-600;
    transition: transform 0.2s ease;
  }

  .TheNavbarMobile__dropdown {
    background-color: $color-ui-gray-cool-50;
    border-top: 1px solid $color-ui-gray-cool-200;
  }

  .TheNavbarMobile__dropdown-list {
    list-style: none;
    margin: 0;
    padding: 0.5rem 0;
  }

  .TheNavbarMobile__dropdown-item {
    border-bottom: 1px solid $color-ui-gray-cool-100;

    &:last-child {
      border-bottom: none;
    }
  }

  .TheNavbarMobile__dropdown-link {
    display: block;
    padding: 1rem 1.5rem;
    text-decoration: none;
    color: inherit;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: $color-ui-gray-cool-100;
    }
  }

  .TheNavbarMobile__dropdown-link-content {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .TheNavbarMobile__dropdown-link-label {
    @include font-size-16px;
    font-weight: 600;
    color: $color-ui-gray-cool-900;
  }

  .TheNavbarMobile__dropdown-link-description {
    @include font-size-14px;
    color: $color-ui-gray-cool-600;
    line-height: 1.4;
  }

  .TheNavbarMobile__actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1.5rem;
    border-top: 1px solid $color-ui-gray-cool-200;
    flex-shrink: 0;
  }

  .TheNavbarMobile__action {
    width: 100%;
  }
}
</style>
